<template>
    <div ref="languageButtonRef"
         @click="openModal">
        <div class="currency-selection">
            <img v-if="locales"
                 :src="asset_cdn(locales[currentLocale].logo, 15)"
                 width="15"
                 height="15"
                 :alt="currentCurrency.name">
            <div class="country-initials">
                {{ currentLocale }} -&nbsp;
            </div>
            <div>
                {{ currentCurrency.currency_code }}
            </div>
            <div>
                <i class="angle-down fa-solid fa-angle-down" />
            </div>
        </div>
    </div>
    <Teleport to="body">
        <Transition name="modal-fade">
            <CurrencyLanguageSearchModal v-if="showModal" />
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
    import { h, ref, watchEffect } from 'vue';
    import { asset_cdn } from '@/js/helpers/helpers';
    import useTippyPopup from '../../composables/useTippyPopup';
    import CurrencyLanguageSearchContent from './CurrencyLanguageSearchContent.vue';
    import { useDeviceSize } from '@/js/composables/useDeviceSize';
    import { useMenuStore } from '@/js/store/menu/MenuStore';
    import CurrencyLanguageSearchModal from '../menu/mobile-menu-footer/search/CurrencyLanguageSearchModal.vue';
    import { ICurrencyLanguageSearch } from '../menu/types/menuFooterTypes';

    interface ICurrencyLanguageSearchProps extends ICurrencyLanguageSearch {
        showModal?: boolean,
        isShort?: boolean,
    }

    const props = defineProps<ICurrencyLanguageSearchProps>();

    const menuStore = useMenuStore();
    const { isMobileOrTablet } = useDeviceSize();

    const languageButtonRef = ref(null);

    const openModal = () => {
        menuStore.isCurrencySearchModalOpen = true;
    };

    const { disable, enable } = useTippyPopup(languageButtonRef, {
        content: h(CurrencyLanguageSearchContent, {
            currencies: props.currencies,
            currentCurrency: props.currentCurrency,
            currentLocale: props.currentLocale,
            locales: props.locales,
            currentRouteUrls: props.currentRouteUrls,
            isShort: props.isShort,
        }),
        placement: 'top',
        trigger: 'click',
        interactive: true,
    });

    watchEffect(() => {
        if (isMobileOrTablet.value) {
            disable();
        } else {
            enable();
        }
    });
</script>

<style scoped lang="scss">
@use "@/sass/design-system/variables" as variables;

.modal-fade-enter-active,
.modal-fade-leave-active {
    transition: opacity 300ms ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
    opacity: 0;
}

.currency-selection {
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 1000ms;

    img {
        border: 1px solid variables.$baseWhite;
        border-radius: 50%;
        margin-right: 4px;
    }

    &:hover {
        color: #d7d3ef;
    }

    .country-initials {
        text-transform: uppercase;
    }

    .angle-down {
        padding-left: 4px;
    }
}

.dark-theme {
    .currency-selection {
        color: variables.$baseWhite;

        &:hover {
            color: #d7d3ef;
        }
    }
}
</style>
